import {
  useMemo,
  useRef,
  useState,
  useCallback,
  ReactElement,
  useLayoutEffect,
  useEffect,
} from 'react';
import {Grid} from 'react-window';
import {styled} from 'styled-components';

import {debounce} from '@shared_web/lib/debounce';
import {useStateRef} from '@shared_web/lib/use_state_ref';

interface VirtualizedGridProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  renderMeasurementItem: () => React.ReactNode;
  getItemKey?: (item: T, index: number) => string | number;
  minColumnWidth: number;
  gap: number;
  overscanRowCount?: number;
  overscanColumnCount?: number;
}

export function VirtualizedGrid<T>(props: VirtualizedGridProps<T>) {
  const {
    items,
    renderItem,
    renderMeasurementItem,
    getItemKey: _getItemKey,
    minColumnWidth,
    gap,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [measuredRowHeight, setMeasuredRowHeight, measuredRowHeightRef] = useStateRef<
    number | null
  >(null);

  // Calculate column count and column width
  const {columnCount, columnWidth} = useMemo(() => {
    if (availableWidth <= 0) {
      return {columnCount: 1, columnWidth: minColumnWidth};
    }
    const maxColumns = Math.floor(availableWidth / (minColumnWidth + gap));
    const columnCount = Math.max(1, maxColumns);
    const columnWidth = (availableWidth - (columnCount - 1) * gap) / columnCount + gap;
    return {columnCount, columnWidth: Math.max(minColumnWidth, columnWidth)};
  }, [availableWidth, minColumnWidth, gap]);

  // Calculate row count
  const rowCount = useMemo(() => {
    if (columnCount === 0) return 0;
    return Math.ceil(items.length / columnCount);
  }, [items.length, columnCount]);

  // Measure row height when columnWidth changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to measure again when renderMeasurementItem or columnWidth changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (measuredRowHeightRef.current !== measurementRef.current?.clientHeight) {
        setMeasuredRowHeight(measurementRef.current?.clientHeight ?? null);
      }
    }, 200);
    return () => {
      clearInterval(interval);
    };
  }, [measuredRowHeightRef, measurementRef]);

  // Update container width and height on resize
  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const updateSize = debounce(() => {
      setAvailableWidth(window.innerWidth);
    }, 100);

    setAvailableWidth(window.innerWidth);
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const Cell = useCallback(
    (props: {
      columnIndex: number;
      rowIndex: number;
      style: React.CSSProperties;
      ariaAttributes: {
        'aria-colindex': number;
        role: 'gridcell';
      };
    }): ReactElement => {
      const {columnIndex, rowIndex, style, ariaAttributes} = props;
      const index = rowIndex * columnCount + columnIndex;
      if (index >= items.length) {
        return <div style={style} {...ariaAttributes} />;
      }

      const item = items[index];
      return (
        <CellWrapper
          style={{
            ...style,
            width: typeof style.width === 'number' ? style.width - gap : style.width,
            height: typeof style.height === 'number' ? style.height - gap : style.height,
          }}
          {...ariaAttributes}
        >
          {item ? renderItem(item, index) : <div />}
        </CellWrapper>
      );
    },
    [items, columnCount, gap, renderItem]
  );

  if (items.length === 0) {
    return null;
  }

  if (!containerRef.current) {
    return <Container ref={containerRef} />;
  }

  return (
    <Container ref={containerRef}>
      {/* Always render measurement element (hidden) so it can resize with columnWidth */}
      <MeasurementWrapper
        ref={measurementRef}
        style={{
          width: columnWidth,
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          top: '-9999px',
          left: '-9999px',
        }}
      >
        {renderMeasurementItem()}
      </MeasurementWrapper>
      {/* Render grid when we have a measured height */}
      {measuredRowHeight !== null && (
        <Grid
          columnCount={columnCount}
          columnWidth={columnWidth}
          rowCount={rowCount}
          rowHeight={measuredRowHeight + gap}
          style={{height: '100%', width: '100%', overflowX: 'hidden'}}
          overscanCount={2}
          cellComponent={Cell}
          cellProps={{}}
        />
      )}
    </Container>
  );
}

VirtualizedGrid.displayName = 'VirtualizedGrid';

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const CellWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
`;

const MeasurementWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;
